import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, Table, Select, Tag, Alert, Space, Typography, Empty, Dropdown, Segmented, Tabs, Button, message } from 'antd';
import { CloudDownloadOutlined, DownOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../api';
import PairSwapPanel from './PairSwapPanel';
import { exportCsv } from '../utils/exportCsv';
import { useI18n } from '../i18n';

// 本地记录状态 → 标签颜色
const STATUS_TAG = {
  pending: { color: 'processing', text: '进行中' },
  success: { color: 'success', text: '成功' },
  failed: { color: 'error', text: '失败' }
};

// token 合约地址 → 符号（本地记录的支出/换得列按方向显示）
const TOKEN_SYMBOL_BY_CONTRACT = {
  '0x55d398326f99059ff775485246999027b3197955': 'USDT',
  '0x6055f4ec69c1461c114af2c5cd629921894b24e7': 'USGNAA'
};
const symbolOf = (contract) => TOKEN_SYMBOL_BY_CONTRACT[(contract || '').toLowerCase()] || '';

// 已知合约地址 → 显示名称（与 backend/config/constants.js 保持一致）
const KNOWN_CONTRACTS = {
  '0x1b0a4c32ef6f3e69bb655085335782713c032276': 'ALOMOLA-USDT (Cake-LP)',
  '0x10ed43c718714eb63d5aa57b78b54704e256024e': 'PancakeSwap Router',
  '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 'WBNB'
};

// 本地记录与链上记录上下排列展示；focus={address, tick} 时选中对应钱包的链上记录
const TransactionHistory = ({ wallets, refreshTick, focus, addressBook = [] }) => {
  const { t, ts } = useI18n();
  const [localTxs, setLocalTxs] = useState([]);
  const [chainTxs, setChainTxs] = useState([]);
  const [chainAddress, setChainAddress] = useState('');
  // 链上记录数据源：live=实时抓取，archive=本地存档
  const [chainSource, setChainSource] = useState('live');
  // 链上记录 card 内部 tab：transfers=钱包转账，pairswaps=PancakeSwap 成交
  const [chainTab, setChainTab] = useState('transfers');
  const [syncing, setSyncing] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [chainLoading, setChainLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [chainError, setChainError] = useState('');
  // 防护竞态：快速切换钱包/数据源时，先发出的请求可能晚于后发出的请求返回，
  // 用自增请求序号标记"最新一次"请求，仅当响应对应最新序号时才写入 state
  const chainRequestIdRef = useRef(0);

  const loadLocal = useCallback(async () => {
    setLocalLoading(true);
    setLocalError('');
    try {
      const res = await api.get('/transactions');
      setLocalTxs(res.data.data);
    } catch (err) {
      setLocalError(err.response?.data?.error || err.message);
    } finally {
      setLocalLoading(false);
    }
  }, []);

  const loadChain = useCallback(async (address, source) => {
    if (!address) return;
    const requestId = ++chainRequestIdRef.current;
    setChainLoading(true);
    setChainError('');
    try {
      const url = source === 'archive'
        ? `/transactions/onchain/${address}/local`
        : `/transactions/onchain/${address}`;
      const res = await api.get(url);
      // 若等待期间用户已切换钱包或数据源，本次响应已过期，丢弃不写入 state
      if (requestId !== chainRequestIdRef.current) return;
      setChainTxs(res.data.data);
    } catch (err) {
      if (requestId !== chainRequestIdRef.current) return;
      setChainError(err.response?.data?.error || err.message);
    } finally {
      if (requestId === chainRequestIdRef.current) setChainLoading(false);
    }
  }, []);

  // 同步链上数据到本地数据库：latest 只抓最新首页（快），full 翻页抓全量
  const handleSync = async (mode) => {
    if (!chainAddress) return;
    setSyncing(true);
    try {
      const res = await api.post(`/transactions/onchain/${chainAddress}/sync`, { mode });
      const { fetched, inserted, totalLocal } = res.data.data;
      message.success(
        t(
          mode === 'latest'
            ? '增量同步完成：抓取 {fetched} 条，新增 {inserted} 条，本地共 {total} 条'
            : '全量同步完成：抓取 {fetched} 条，新增 {inserted} 条，本地共 {total} 条',
          { fetched, inserted, total: totalLocal }
        )
      );
      if (chainSource === 'archive') loadChain(chainAddress, 'archive');
    } catch (err) {
      message.error(t('同步失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadLocal();
  }, [loadLocal, refreshTick]);

  useEffect(() => {
    loadChain(chainAddress, chainSource);
  }, [chainAddress, chainSource, loadChain]);

  // 钱包列表"查看交易"跳转：切到转账 tab 并选中对应钱包的链上记录
  useEffect(() => {
    if (focus?.address) {
      setChainAddress(focus.address);
      setChainTab('transfers');
    }
  }, [focus]);

  // 地址 → 钱包标签映射：命中 10 个钱包显示 #x_x 名称，其他显示地址
  const walletNameMap = useMemo(
    () => Object.fromEntries(wallets.map((w) => [w.address.toLowerCase(), w.label])),
    [wallets]
  );

  // 地址簿映射：设置里自定义的地址名称
  const bookNameMap = useMemo(
    () => Object.fromEntries(addressBook.map((e) => [e.address.toLowerCase(), e.name])),
    [addressBook]
  );

  // 地址簿中标记为内部的地址（排除与 10 个固定钱包重复的），一并提供链上记录查询
  const internalBookEntries = useMemo(() => {
    const own = new Set(wallets.map((w) => w.address.toLowerCase()));
    return addressBook.filter((e) => e.isInternal && !own.has(e.address.toLowerCase()));
  }, [addressBook, wallets]);

  // 地址 → 纯文本显示名（下拉选项等不能放组件的场景用），优先级与 renderParty 一致
  const nameOf = useCallback(
    (value) => {
      const key = (value || '').toLowerCase();
      return (
        walletNameMap[key] ||
        bookNameMap[key] ||
        KNOWN_CONTRACTS[key] ||
        `${value.slice(0, 8)}...${value.slice(-6)}`
      );
    },
    [walletNameMap, bookNameMap]
  );

  // 渲染转账参与方，优先级：自有钱包 > 地址簿 > 已知合约 > 截断地址（可复制）
  const renderParty = (value) => {
    if (!value) return '-';
    const label = walletNameMap[value.toLowerCase()];
    if (label) return <Tag color="blue">{label}</Tag>;
    const bookName = bookNameMap[value.toLowerCase()];
    if (bookName) return <Tag color="gold">{bookName}</Tag>;
    const contractName = KNOWN_CONTRACTS[value.toLowerCase()];
    if (contractName) return <Tag color="purple">{contractName}</Tag>;
    if (/^0x[0-9a-fA-F]{40}$/.test(value)) {
      return (
        <Typography.Text style={{ fontFamily: 'monospace' }} copyable={{ text: value }}>
          {value.slice(0, 8)}...{value.slice(-6)}
        </Typography.Text>
      );
    }
    return value;
  };

  // 导出当前钱包的链上转账记录（导出当前数据源下的全部记录）
  const handleExportChain = () => {
    const owner = nameOf(chainAddress);
    exportCsv({
      filename: `链上转账_${owner}_${chainSource === 'archive' ? '本地存档' : '实时抓取'}`,
      columns: [
        { title: '时间', value: (t) => new Date(t.timestamp).toLocaleString() },
        { title: '方向', value: (t) => (t.direction === 'out' ? '转出' : '转入') },
        { title: '从名称', value: (t) => (t.from ? nameOf(t.from) : '') },
        { title: '从地址', value: (t) => t.from || '' },
        { title: '到名称', value: (t) => (t.to ? nameOf(t.to) : '') },
        { title: '到地址', value: (t) => t.to || '' },
        { title: '数量', value: (t) => t.amount },
        { title: '币种', value: (t) => t.tokenSymbol },
        { title: '交易哈希', value: (t) => t.txHash }
      ],
      rows: chainTxs
    });
  };

  const localColumns = [
    {
      title: t('时间'),
      dataIndex: 'created_at',
      width: 170,
      // 优先用后端换算好的真实纪元毫秒（按浏览器时区展示）；旧后端无该字段时回退原值
      render: (v, row) => new Date(row.created_at_ms ?? v).toLocaleString()
    },
    {
      title: t('钱包_col'),
      dataIndex: 'wallet_address',
      render: (a) => renderParty(a)
    },
    {
      title: t('来源'),
      dataIndex: 'source',
      width: 80,
      render: (s) => (s === 'auto' ? <Tag color="purple">{t('自动')}</Tag> : <Tag>{t('手动')}</Tag>)
    },
    {
      title: t('支出'),
      dataIndex: 'amount',
      align: 'right',
      width: 140,
      render: (v, row) => `${v} ${symbolOf(row.from_token)}`
    },
    {
      title: t('换得'),
      dataIndex: 'amount_out',
      align: 'right',
      width: 160,
      render: (v, row) => (v == null ? '-' : `${v} ${symbolOf(row.to_token)}`)
    },
    {
      title: t('状态'),
      dataIndex: 'status',
      width: 100,
      render: (s) => {
        const st = STATUS_TAG[s] || { color: 'default', text: s };
        return <Tag color={st.color}>{t(st.text)}</Tag>;
      }
    },
    {
      title: t('交易'),
      dataIndex: 'tx_hash',
      render: (hash, row) =>
        hash ? (
          <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noreferrer">
            {hash.slice(0, 12)}...
          </a>
        ) : row.error_message ? (
          <Typography.Text type="danger" ellipsis={{ tooltip: row.error_message }} style={{ maxWidth: 220 }}>
            {row.error_message}
          </Typography.Text>
        ) : (
          '-'
        )
    }
  ];

  const chainColumns = [
    {
      title: t('时间'),
      dataIndex: 'timestamp',
      width: 170,
      render: (v) => new Date(v).toLocaleString()
    },
    {
      title: t('方向'),
      dataIndex: 'direction',
      width: 90,
      render: (d) => (d === 'out' ? <Tag color="red">{t('转出')}</Tag> : <Tag color="green">{t('转入')}</Tag>)
    },
    {
      title: t('从 → 到'),
      key: 'party',
      render: (_, t) => (
        <Space size={4} wrap>
          {renderParty(t.from)}
          <span>→</span>
          {renderParty(t.to)}
        </Space>
      )
    },
    {
      title: t('数量'),
      key: 'amount',
      align: 'right',
      render: (_, row) => `${Number(row.amount).toFixed(4)} ${row.tokenSymbol}`
    },
    {
      title: t('交易'),
      dataIndex: 'txHash',
      render: (hash, t) => (
        <a href={t.bscscanUrl} target="_blank" rel="noreferrer">
          {hash.slice(0, 16)}...
        </a>
      )
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={t('本地记录')}
        extra={
          <Button icon={<ReloadOutlined />} onClick={loadLocal} loading={localLoading}>
            {t('刷新')}
          </Button>
        }
      >
        {localError && <Alert type="error" showIcon message={ts(localError)} style={{ marginBottom: 16 }} />}
        <Table
          rowKey="transaction_id"
          columns={localColumns}
          dataSource={localTxs}
          loading={localLoading}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: <Empty description={t('暂无记录')} /> }}
        />
      </Card>

      <Card title={t('链上记录')}>
        <Tabs
          activeKey={chainTab}
          onChange={setChainTab}
          items={[
            {
              key: 'transfers',
              label: t('钱包转账'),
              children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Select
              style={{ minWidth: 280 }}
              placeholder={t('选择钱包查询链上记录')}
              value={chainAddress || undefined}
              onChange={(v) => setChainAddress(v)}
              showSearch
              filterOption={(input, opt) =>
                String(opt.label).toLowerCase().includes(input.toLowerCase()) ||
                String(opt.value).toLowerCase().includes(input.toLowerCase())
              }
              options={[
                {
                  label: t('我的钱包'),
                  options: wallets.map((w) => ({
                    value: w.address,
                    label: `${w.label}  ${w.address.slice(0, 10)}...${w.address.slice(-6)}`
                  }))
                },
                ...(internalBookEntries.length
                  ? [
                      {
                        label: t('内部地址（地址簿）'),
                        options: internalBookEntries.map((e) => ({
                          value: e.address,
                          label: `${e.name}  ${e.address.slice(0, 10)}...${e.address.slice(-6)}`
                        }))
                      }
                    ]
                  : [])
              ]}
            />
            <Segmented
              value={chainSource}
              onChange={setChainSource}
              options={[
                { label: t('实时抓取'), value: 'live' },
                { label: t('本地存档'), value: 'archive' }
              ]}
            />
            <Dropdown.Button
              icon={<DownOutlined />}
              onClick={() => handleSync('latest')}
              loading={syncing}
              disabled={!chainAddress}
              menu={{
                items: [
                  { key: 'full', icon: <CloudDownloadOutlined />, label: t('全量同步（翻页抓取全部历史，较慢）') }
                ],
                onClick: () => handleSync('full')
              }}
            >
              {t('同步最新')}
            </Dropdown.Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportChain}
              disabled={!chainAddress || !chainTxs.length}
            >
              {t('导出 Excel')}
            </Button>
            {chainAddress && (
              <a href={`https://bscscan.com/address/${chainAddress}`} target="_blank" rel="noreferrer">
                {t('在 BscScan 查看该地址全部记录')}
              </a>
            )}
          </Space>
          <Alert
            type="info"
            showIcon
            message={
              chainSource === 'live'
                ? t('记录实时抓取自 BscTrace（最新在前）；"同步最新"只抓最新一页入库，下拉可选全量同步')
                : t('本地存档：历次同步累积的链上记录（已去重，最新在前）')
            }
          />
          {chainError && <Alert type="error" showIcon message={ts(chainError)} />}
          {chainAddress ? (
            <Table
              rowKey={(t) => `${t.txHash}-${t.tokenSymbol}-${t.timestamp}-${t.direction}`}
              columns={chainColumns}
              dataSource={chainTxs}
              loading={chainLoading}
              size="middle"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: <Empty description={t('该地址近期无相关链上记录')} /> }}
            />
          ) : (
            <Empty description={t('请先选择钱包')} />
          )}
        </Space>
              )
            },
            {
              key: 'pairswaps',
              label: t('PancakeSwap 成交'),
              children: <PairSwapPanel renderParty={renderParty} nameOf={nameOf} />
            }
          ]}
        />
      </Card>
    </Space>
  );
};

export default TransactionHistory;
